<template>
  <div>
    <b-row class="d-flex align-items-center">
      <b-col cols="8">
        <h2 v-if="!$route.params.id" class="font-weight-bolder mb-0">
          Create New Segment
        </h2>
        <h2 v-else class="font-weight-bolder mb-0">
          Edit Segment: {{ segmentSelected.name }}
        </h2>
      </b-col>
      <b-col cols="4" class="text-right d-flex justify-content-end">
        <b-button
          :disabled="loadingPreview"
          variant="outline-secondary"
          class="mr-1"
          @click="handleLoadPreview"
          style="min-width: 152px"
        >
          <feather-icon icon="EyeIcon" />
          <template>
            <Spinner v-if="loadingPreview" variant="primary" class="ml-2" />
            <span v-else class="text-lg text-nowrap" style="margin-left: 4px"
              >Preview Size</span
            >
          </template>
        </b-button>
        <b-button
          size="md"
          variant="primary"
          class="mr-2"
          @click="handleCreateNewSegment"
          style="min-width: 165px"
        >
          <feather-icon icon="SaveIcon" />
          <template>
            <Spinner v-if="loadingUpsertSegment" />
            <span v-else class="text-lg text-nowrap" style="margin-left: 4px"
              >Save {{ !$route.params.id ? "Segment" : "changes" }}
            </span>
          </template>
        </b-button>
        <div
          class="cursor-pointer"
          @click="$router.push('/marketing/segments-customer')"
        >
          <feather-icon icon="XIcon" size="24" />
        </div>
      </b-col>
    </b-row>

    <b-row class="mt-2">
      <b-col cols="12" md="9">
        <b-card>
          <validation-observer ref="simpleRules">
            <h5 class="" style="font-weight: 800">Basic Information</h5>

            <b-form-group class="mt-2">
              <p class="mb-0 font-weight-bold">Segment Name</p>
              <validation-provider
                #default="{ errors }"
                name="segment name"
                rules="required"
              >
                <b-form-input
                  v-model="form.segmentName"
                  placeholder="Enter segment name..."
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group>
              <p class="mb-0 font-weight-bold">Description</p>
              <validation-provider
                #default="{ errors }"
                name="description"
                rules="required"
              >
                <b-form-textarea
                  v-model="form.description"
                  placeholder="Describe the purpose and characteristics of this segment..."
                  rows="3"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group>
              <p class="mb-0 font-weight-bold">Status</p>
              <v-select
                :value="form.status"
                :options="statusOptions"
                :reduce="(val) => val"
                :clearable="false"
                @input="(val) => (form.status = val)"
                class="w-100"
              />
            </b-form-group>
          </validation-observer>
        </b-card>

        <b-row>
          <b-col cols="12">
            <template>
              <b-card>
                <h5 class="mb-1" style="font-weight: 800">Segment Criteria</h5>
                <div class="d-flex align-items-center mb-1" style="gap: 10px">
                  <p class="m-0">Combine groups using</p>
                  <div class="d-flex align-items-center">
                    <button
                      style="
                        border-radius: 4px 0 0 4px;
                        border: 1px solid #6c757d;
                        background: white;
                        color: #6c757d;
                      "
                      :class="{ 'active-logic': !logicGroups }"
                      @click="logicGroups = false"
                    >
                      AND
                    </button>
                    <button
                      style="
                        border-radius: 0 4px 4px 0;
                        border: 1px solid #6c757d;
                        background: white;
                        color: #6c757d;
                      "
                      :class="{ 'active-logic': logicGroups }"
                      @click="logicGroups = true"
                    >
                      OR
                    </button>
                  </div>
                </div>
                <GroupRow
                  v-for="(group, index) in groups"
                  :key="group.id"
                  :order="index + 1"
                  :group-length="groups.length"
                  :group="group"
                  @removeCondition="removeCondition"
                  @addCondition="addCondition"
                  @updateField="updateField"
                  @updateOperator="updateOperator"
                  @updateValue="updateValue"
                  @toggleConditionLogic="toggleConditionLogic"
                  :operators="operators"
                  :fields="fields"
                />

                <b-button
                  variant="outline-primary"
                  @click="addGroup"
                  style="padding: 6px 8px"
                >
                  <feather-icon icon="PlusIcon" /> Add Group</b-button
                >
              </b-card>
            </template>
          </b-col>
        </b-row>

        <!-- MERGE SEGMENT -->

        <b-row v-if="listMergeSegFormat.length > 0">
          <b-col cols="12">
            <template>
              <b-card v-for="(item, ind) in listMergeSegFormat" :key="ind">
                <div class="d-flex align-items-center justify-content-between">
                  <h5 class="mb-1" style="font-weight: 800">
                    Merge Segment {{ ind + 1 }}: {{ item.name }}
                  </h5>
                  <b-button
                    @click="removeSegment(item.id)"
                    variant="outline-danger"
                    style="padding: 6px"
                  >
                    <feather-icon icon="TrashIcon" size="20" />
                  </b-button>
                </div>
                <div class="d-flex align-items-center mb-1" style="gap: 10px">
                  <p class="m-0">Combine groups using</p>
                  <div class="d-flex align-items-center">
                    <button
                      style="
                        border-radius: 4px 0 0 4px;
                        border: 1px solid #6c757d;
                        background: white;
                        color: #6c757d;
                      "
                      :class="{ 'active-logic': !item.logic }"
                      @click="item.logic = false"
                    >
                      AND
                    </button>
                    <button
                      style="
                        border-radius: 0 4px 4px 0;
                        border: 1px solid #6c757d;
                        background: white;
                        color: #6c757d;
                      "
                      :class="{ 'active-logic': item.logic }"
                      @click="item.logic = true"
                    >
                      OR
                    </button>
                  </div>
                </div>
                <GroupRow
                  v-for="(group, index) in item.groups"
                  :key="group.id"
                  :order="index + 1"
                  :group-length="item.groups.length"
                  :group="group"
                  @removeCondition="removeConditionMerge"
                  @addCondition="addConditionMerge"
                  @updateField="updateFieldMerge"
                  @updateOperator="updateOperatorMerge"
                  @updateValue="updateValueMerge"
                  @toggleConditionLogic="toggleConditionLogicMerge"
                  :operators="operators"
                  :fields="fields"
                />

                <b-button
                  variant="outline-primary"
                  @click="addGroupMerge(item)"
                  style="padding: 6px 8px"
                >
                  <feather-icon icon="PlusIcon" /> Add Group</b-button
                >
              </b-card>
            </template>
          </b-col>
        </b-row>

        <!-- SELECT MERGE SEGMENT -->
        <MergeExistingSegments
          :selected-id="selectedId"
          :selected-segments="selectedSegments"
          @handleSegmentSelect="handleSegmentSelect"
          @removeSegment="removeSegment"
          @toggleSegmentLogic="toggleSegmentLogic"
        />
      </b-col>

      <b-col cols="12" md="3">
        <b-row>
          <b-col cols="12">
            <b-card>
              <div class="text-center">
                <h5 class="text-body text-left" style="font-weight: 900">
                  Segment Preview
                </h5>
                <div v-if="dataPreview === null">
                  <div class="mt-1 mb-1">
                    <feather-icon icon="EyeIcon" size="24" class="text-muted" />
                  </div>
                  <span class="text-muted"
                    >Click "Preview Size" to see estimated segment size</span
                  >
                </div>
                <div v-else>
                  <h1>{{ dataPreview }}</h1>
                  <p>Estimated customers</p>
                  <div
                    class="small"
                    style="
                      gap: 5px;
                      padding: 8px;
                      border: 1px solid #b6effb;
                      background: #cff4fc;
                    "
                  >
                    <feather-icon
                      icon="AlertCircleIcon"
                      size="20"
                      class="text-white"
                      style="
                        background: #055160;
                        border-radius: 100%;
                        margin-right: 5px;
                      "
                    />
                    This is an estimate based on current data and merged
                    segments.
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>

          <b-col cols="12">
            <b-card>
              <h5 class="text-body text-left" style="font-weight: 900">
                Access Control
              </h5>
              <span class="mt-3 text-muted">No users specified</span>
            </b-card>
          </b-col>

          <b-col cols="12">
            <b-card>
              <h5 class="text-body text-left" style="font-weight: 900">
                Summary
              </h5>
              <div class="d-flex flex-column">
                <div class="d-flex justify-content-between">
                  <span class="text-muted">Criteria Groups:</span>
                  <strong>{{ groups.length }}</strong>
                </div>
                <div class="d-flex justify-content-between">
                  <span class="text-muted">Total Conditions:</span>
                  <strong>1</strong>
                </div>
                <div class="d-flex justify-content-between">
                  <span class="text-muted">Merged Segments:</span>
                  <strong>{{ selectedSegments.length }}</strong>
                </div>
                <div class="d-flex justify-content-between">
                  <span class="text-muted">Group Logic:</span>
                  <strong>{{ logicGroups ? "OR" : "AND" }}</strong>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Spinner from "@core/components/Spinner";
import {
  BButton,
  BCard,
  BCol,
  BPagination,
  BRow,
  BButtonGroup,
  BTable,
  BCardBody,
  BFormInput,
  BMedia,
  BAvatar,
  BProgress,
  BProgressBar,
  BFormTextarea,
  BFormCheckbox,
  BFormRadioGroup,
  BFormRadio,
  BFormGroup,
  BImg,
  BFormCheckboxGroup,
  BFormSelect,
  BBadge,
  BCollapse,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
import GroupRow from "./GroupRow.vue";
import MergeExistingSegments from "./MergeExistingSegments.vue";
import { VBToggle } from "bootstrap-vue";
import { required } from "@validations";
import useToastHandler from "@/services/toastHandler";
import formValidation from "@/@core/comp-functions/forms/form-validation";
import { mapState, mapActions } from "vuex";
import { fieldsCustomerSegment } from "@/@core/utils/enum/user";
import { v4 as uuidv4 } from "uuid";

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BRow,
    BCol,
    BTable,
    BMedia,
    BPagination,
    vSelect,
    Spinner,
    BButtonGroup,
    BTable,
    BCardBody,
    BFormInput,
    BAvatar,
    flatPickr,
    BProgress,
    BProgressBar,
    ValidationProvider,
    ValidationObserver,
    BFormTextarea,
    BFormCheckbox,
    BFormRadioGroup,
    BFormRadio,
    BFormGroup,
    BImg,
    BFormCheckboxGroup,
    BFormSelect,
    FeatherIcon,
    GroupRow,
    BCollapse,
    MergeExistingSegments,
  },
  directives: {
    "b-toggle": VBToggle,
  },
  data() {
    const fields = fieldsCustomerSegment;

    const operators = [
      { label: "Equals", value: "=" },
      { label: "Greater than", value: ">" },
      { label: "Less Than", value: "<" },
      { label: "Greater Than or Equal", value: ">=" },
      { label: "Less Than or Equal", value: "<=" },
      { label: "Not Equal", value: "!=" },
      { label: "Contains", value: "CONTAINS" },
      { label: "In", value: "IN" },
    ];

    return {
      form: {
        segmentName: "",
        description: "",
        accessControl: "",
        status: "active",
      },
      statusOptions: ["active", "inactive", "paused"],
      fields,
      operators,
      groups: [this.createGroup(1)],
      selectedSegments: [],
      selectedId: null,
      dataPreview: null,

      segmentSelected: null,
      logicGroups: false,

      listMergeSegFormat: [],
    };
  },
  computed: {
    ...mapState("segment/list", ["loadingUpsertSegment", "loadingPreview"]),
  },
  async mounted() {
    await this.fetchSegmentList();
  },
  watch: {
  },
  methods: {
    ...mapActions({
      upsertSegment: "segment/list/upsertSegment",
      previewSegment: "segment/list/previewSegment",
      getSegmentsList: "segment/list/getSegmentsList",
    }),
    createCondition(order = 1) {
      return {
        id: uuidv4(),
        displayOrder: order,
        field: "",
        operator: "=",
        value: "",
      };
    },

    createGroup(order = 1) {
      return {
        id: uuidv4(),
        displayOrder: order,
        conditions: [this.createCondition(1)],
        logic: false,
      };
    },
    addGroup() {
      const maxOrder = this.groups.length
        ? Math.max(...this.groups.map((g) => g.displayOrder))
        : 0;
      this.groups.push(this.createGroup(maxOrder + 1));
    },
    addCondition(groupId) {
      const group = this.groups.find((g) => g.id === groupId);
      if (!group) return;

      const maxOrder = group.conditions.length
        ? Math.max(...group.conditions.map((c) => c.displayOrder || 0))
        : 0;

      group.conditions.push(this.createCondition(maxOrder + 1));
    },
    removeCondition(groupId, conditionId) {
      const group = this.groups.find((g) => g.id === groupId);
      if (!group) return;

      if (!conditionId) {
        this.groups = this.groups.filter((g) => g.id !== groupId);
      } else {
        group.conditions = group.conditions.filter((c) => c.id !== conditionId);
      }
    },
    updateField(val, groupId, conditionId) {
      const cond = this.getCondition(groupId, conditionId);
      if (cond) cond.field = val;
    },

    updateOperator(val, groupId, conditionId) {
      const cond = this.getCondition(groupId, conditionId);
      if (cond) cond.operator = val;
    },

    updateValue(val, groupId, conditionId) {
      const cond = this.getCondition(groupId, conditionId);
      if (cond) cond.value = val;
    },

    toggleConditionLogic(val, groupId, conditionId) {
      this.groups = this.groups.map((group) => {
        if (group.id !== groupId) return group;

        if (!conditionId) {
          return { ...group, logic: val };
        }

        const updatedConditions = group.conditions.map((c) => {
          return c.id === conditionId ? { ...c, logic: val } : c;
        });

        return { ...group, conditions: updatedConditions };
      });
    },

    getCondition(groupId, conditionId) {
      const group = this.groups.find((g) => g.id === groupId);
      return group?.conditions.find((c) => c.id === conditionId);
    },

    handleSegmentSelect(id, list) {
      const segment = list.find((s) => s.id === id);
      if (segment && !this.selectedSegments.find((s) => s.id === id)) {
        this.selectedSegments.push({ ...segment, logic: true });

        // FORMAT LIST MERGE
        const newList = segment.query.map((i) => {
          return {
            id: segment.id,
            name: segment.name,
            logic: i.logicalBetweenGroups === "OR",
            groups: i.groups.map((group, index) => {
              return {
                id: uuidv4(),
                displayOrder: index + 1,
                logic: group.logicalBetweenQueries === "OR",
                conditions: group.conditions.map((condition, ind) => {
                  return {
                    id: uuidv4(),
                    displayOrder: ind + 1,
                    field: condition.field,
                    operator: condition.operator,
                    value: condition.value,
                  };
                }),
              };
            }),
          };
        });
        this.listMergeSegFormat.push(...newList);
      }
      this.selectedId = null;
    },
    removeSegment(id) {
      this.selectedSegments = this.selectedSegments.filter((s) => s.id !== id);
      this.listMergeSegFormat = this.listMergeSegFormat.filter(
        (s) => s.id !== id
      );
    },
    toggleSegmentLogic(id, val) {
      const seg = this.selectedSegments.find((s) => s.id === id);
      if (seg) seg.logic = val;
    },
    async handleCreateNewSegment() {
      const valid = await this.$refs.simpleRules.validate();
      if (!valid) return;
      if (
        this.groups.length === 0 ||
        this.groups.some((i) =>
          i.conditions.some(
            (item) => !item.field || !item.operator || !item.value
          )
        )
      ) {
        return this.showToastError(
          "Please fill enough data in each group!",
          "Error"
        );
      }

      const list = this.listMergeSegFormat.map((i) => {
        return {
          logicalBetweenGroups: i.logic ? "OR" : "AND",
          groups: i.groups.map((group) => {
            return {
              logicalBetweenQueries: group.logic ? "OR" : "AND",
              conditions: group.conditions.map((item) => {
                return {
                  field: item.field,
                  operator: item.operator,
                  value: item.value,
                };
              }),
            };
          }),
        };
      });

      const data = {
        name: this.form.segmentName,
        description: this.form.description,
        status: this.form.status.toLowerCase(),
        ...(this.$route.params.id && { id: this.$route.params.id }),
        query: [
          {
            logicalBetweenGroups: this.logicGroups ? "OR" : "AND",
            groups: this.groups.map((group) => {
              return {
                logicalBetweenQueries: group.logic ? "OR" : "AND",
                conditions: group.conditions.map((item) => {
                  return {
                    field: item.field,
                    operator: item.operator,
                    value: item.value,
                  };
                }),
              };
            }),
          },
          ...list,
        ],
      };
      const res = await this.upsertSegment(data);
      if (res) {
        this.showToastSuccess(true, "New segment created successfully!");
        this.$router.push("/marketing/segments-customer");
      }
    },
    async handleLoadPreview() {
      const valid = await this.$refs.simpleRules.validate();
      if (!valid) return;
      if (
        this.groups.length === 0 ||
        this.groups.some((i) =>
          i.conditions.some(
            (item) => !item.field || !item.operator || !item.value
          )
        )
      ) {
        return this.showToastError(
          "Please fill enough data in each group!",
          "Error"
        );
      }

      const list = this.listMergeSegFormat.map((i) => {
        return {
          logicalBetweenGroups: i.logic ? "OR" : "AND",
          groups: i.groups.map((group) => {
            return {
              logicalBetweenQueries: group.logic ? "OR" : "AND",
              conditions: group.conditions.map((item) => {
                return {
                  field: item.field,
                  operator: item.operator,
                  value: item.value,
                };
              }),
            };
          }),
        };
      });

      const res = await this.previewSegment({
        name: this.form.segmentName,
        description: this.form.description,
        status: this.form.status.toLowerCase(),
        query: [
          {
            logicalBetweenGroups: this.logicGroups ? "OR" : "AND",
            groups: this.groups.map((group) => {
              return {
                logicalBetweenQueries: group.logic ? "OR" : "AND",
                conditions: group.conditions.map((item) => {
                  return {
                    field: item.field,
                    operator: item.operator,
                    value: item.value,
                  };
                }),
              };
            }),
          },
          ...list,
        ],
      });
      if (res) {
        if (typeof res.usersCount === "number")
          this.dataPreview = res.usersCount;
      }
    },
    async fetchSegmentList() {
      const data = await this.getSegmentsList({
        limit: 100,
      });
      const item = data.data.find((i) => i.id === this.$route.params.id);
      if (item) {
        this.segmentSelected = item;
        this.form = {
          segmentName: item.name,
          description: item.description,
          status: item.status,
        };
        this.dataPreview = item.usersCount;
        this.logicGroups = item.query[0].logicalBetweenGroups === "OR";
        this.groups = item.query[0].groups.map((group, index) => {
          return {
            id: uuidv4(),
            displayOrder: index + 1,
            logic: group.logicalBetweenQueries === "OR",
            conditions: group.conditions.map((condition, ind) => {
              return {
                id: uuidv4(),
                displayOrder: ind + 1,
                field: condition.field,
                operator: condition.operator,
                value: condition.value,
              };
            }),
          };
        });
        if (item.query.length > 1) {
          const newList = [...item.query]
          newList.shift()
          this.listMergeSegFormat = newList.map((i) => {
            return {
              id: item.id,
              name: '',
              logic: i.logicalBetweenGroups === "OR",
              groups: i.groups.map((group, index) => {
                return {
                  id: uuidv4(),
                  displayOrder: index + 1,
                  logic: group.logicalBetweenQueries === "OR",
                  conditions: group.conditions.map((condition, ind) => {
                    return {
                      id: uuidv4(),
                      displayOrder: ind + 1,
                      field: condition.field,
                      operator: condition.operator,
                      value: condition.value,
                    };
                  }),
                };
              }),
            };
          });
        }
      }
    },
    addConditionMerge(groupId) {
      let group;
      this.listMergeSegFormat.forEach((l) => {
        l.groups.forEach((g) => {
          if (g.id === groupId) group = g;
        });
      });
      if (!group) return;

      const maxOrder = group.conditions.length
        ? Math.max(...group.conditions.map((c) => c.displayOrder || 0))
        : 0;

      group.conditions.push(this.createCondition(maxOrder + 1));
    },
    removeConditionMerge(groupId, conditionId) {
      let group;
      this.listMergeSegFormat.forEach((l) => {
        l.groups.forEach((g) => {
          if (g.id === groupId) group = g;
        });
      });
      if (!group) return;
      const groups = this.listMergeSegFormat.find((i) =>
        i.groups.some((gr) => gr.id === groupId)
      );
      if (!conditionId) {
        groups.groups = groups.groups.filter((g) => g.id !== groupId);
      } else {
        group.conditions = group.conditions.filter((c) => c.id !== conditionId);
      }
    },
    getConditionMerge(groupId, conditionId) {
      const groups = this.listMergeSegFormat.find((i) =>
        i.groups.some((gr) => gr.id === groupId)
      );
      const group = groups.groups.find((g) => g.id === groupId);
      return group?.conditions.find((c) => c.id === conditionId);
    },
    updateFieldMerge(val, groupId, conditionId) {
      const cond = this.getConditionMerge(groupId, conditionId);
      if (cond) cond.field = val;
    },

    updateOperatorMerge(val, groupId, conditionId) {
      const cond = this.getConditionMerge(groupId, conditionId);
      if (cond) cond.operator = val;
    },

    updateValueMerge(val, groupId, conditionId) {
      const cond = this.getConditionMerge(groupId, conditionId);
      if (cond) cond.value = val;
    },

    toggleConditionLogicMerge(val, groupId, conditionId) {
      const groups = this.listMergeSegFormat.find((i) =>
        i.groups.some((gr) => gr.id === groupId)
      );
      groups.groups = groups.groups.map((group) => {
        if (group.id !== groupId) return group;

        if (!conditionId) {
          return { ...group, logic: val };
        }

        const updatedConditions = group.conditions.map((c) => {
          return c.id === conditionId ? { ...c, logic: val } : c;
        });

        return { ...group, conditions: updatedConditions };
      });
    },
    addGroupMerge(group) {
      const maxOrder = group.groups.length
        ? Math.max(...group.groups.map((g) => g.displayOrder))
        : 0;
      group.groups.push(this.createGroup(maxOrder + 1));
    },
  },
  setup(_, { emit }) {
    const { showToastSuccess, showToastError } = useToastHandler();
    const { refFormObserver, getValidationState, resetForm } = formValidation(
      () => {}
    );

    const onSubmit = () => {
      emit("next-step");
    };

    return {
      showToastSuccess,
      showToastError,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
      required,
    };
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
.active-logic {
  background: #6c757d !important;
  color: white !important;
}
</style>